import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { makePost } from "../../utils/fetchUtils";
import { FORGOT_PASSWORD } from "../../utils/constants";
import nclude from "../../assets/nclude.svg";
import sticker1 from "../../assets/sticker1.svg";
import curve1 from "../../assets/curve.svg";
import greaterThan from "../../assets/greaterthan.svg";
import useWindowWidth from "../../utils/windowWidth";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLHeadingElement>(null);

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  useEffect(() => {
    document.title = "Forgot Password - Nclude Portal";
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  const validateEmail = (val: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(val);
  };

  const handleRequestResetLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const body = { email: email.trim() };
      const resp = await makePost(FORGOT_PASSWORD, JSON.stringify(body));
      setLoading(false);

      if (resp && resp.success !== false) {
        setSuccessMsg(resp.message || "Password reset link has been sent to your email.");
      } else {
        setError(resp.message || "User with this email address was not found.");
      }
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Error sending password reset link. Please try again.");
      }
    }
  };

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <div className="auth-page flex items-center login-form" style={{ minHeight: isMobile ? "50vh" : "100vh" }} role="main">
      <div className="logo-container">
        <div className="flex justify-center items-center h-screen w-full">
          <div className="flex flex-col items-start">
            <img src={nclude} alt="Nclude logo" className="logo mb-3" />
            <div className="flex flex-col items-start text-container">
              <img src={sticker1} alt="" aria-hidden="true" className="sticker1 mb-2" />
              <img src={curve1} alt="" aria-hidden="true" className="curve1 mb-4" />
              <h3 className="text-left font-normal">
                Resources <br />
                and opportunities for <span className="font-bold tracking-normal text-white">People with Disabilities</span>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-container flex flex-col items-center">
        <div className="auth-form">
          <h1
            className="mt-12 mb-2 text-[2rem]"
            ref={mainRef}
            tabIndex={-1}
            style={{ fontWeight: 600, lineHeight: "2.375rem" }}
          >
            Forgot Password
          </h1>
          <h2
            className="mb-4"
            style={{ color: "#787486", fontWeight: 500, fontSize: "1rem" }}
          >
            Enter your registered email address to receive a password reset magic link.
          </h2>

          {error && (
            <p className="text-center mt-3 text-[#FF0000]" role="alert">
              {error}
            </p>
          )}
          {successMsg && (
            <div className="p-3 my-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-[#15803d] font-medium" role="status">
                {successMsg}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Check your inbox and click the reset link in the email to set a new password.
              </p>
            </div>
          )}

          <form onSubmit={handleRequestResetLink} className="flex flex-col">
            <label htmlFor="reset-email" className="mt-4 sr-only">
              Enter Email Address
            </label>
            <div style={{ position: "relative" }}>
              <label className="border-label">Email Address</label>
            </div>
            <input
              className="w-[100%] h-[5rem] px-4 mt-4 input input-email"
              placeholder="Enter E-mail Address"
              id="reset-email"
              type="email"
              value={email}
              required
              onChange={(e) => {
                setEmail(e.target.value);
                setError("");
                setSuccessMsg("");
              }}
            />

            <button
              type="submit"
              className="mt-8 bg-[#4f00ec] w-[13rem] h-[3.875rem] rounded submit-button"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Reset Link"}
              <img src={greaterThan} alt="" aria-hidden="true" />
            </button>
          </form>

          <h2 style={{ fontSize: "1rem", fontWeight: 500 }} className="mt-8 text-right">
            Remembered your password?{" "}
            <Link to="/login" className="text-[#5030E5]" style={{ textDecoration: "underline" }}>
              Back to Login
            </Link>
          </h2>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
